import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Input } from '@parrot-software/design-system';
import { Group, RefreshCw, Search } from '@parrot-software/design-system/icons';
import { logger } from '@parrot-software/observability';
import { cn } from '@parrot-software/utils';
import { EmptyState, ErrorState, SectionToolbar } from '@subscriptions/presentation/components/base';
import { isAnyBlockConfiguring, useSubscriptionBlocks } from '@subscriptions/presentation/hooks';
import { useAreSubscriptionActionsLocked } from '@subscriptions/presentation/hooks/useAreSubscriptionActionsLocked';
import { ChurnedSubscriptionsBanner } from './ChurnedSubscriptionsBanner';
import { SubscriptionBlockCard } from './SubscriptionBlockCard';
import { SubscriptionBlockCardSkeleton } from './SubscriptionBlockCardSkeleton';
import { isPromoBlock, matchesSearchTerm } from './utils';

const subLog = logger.forDomain('SUB', { subdomain: 'lifecycle' });

/**
 * Artificial "verifying" delay before refetching while a block is configuring. The
 * block's backend configuration runs async, so refetching instantly would very likely
 * return the same (unchanged) configuring state. This short window gives the backend a
 * moment to finish while a button-local spinner acknowledges the click, instead of
 * flashing the skeleton immediately. Mirrors the SPEI "Ya realicé el pago" delay.
 */
const CONFIG_REFRESH_DELAY_MS = 2500;

/**
 * SubscriptionsListContent - Orchestrator for the "Suscripciones" tab
 *
 * Contains toolbar with title, search input, and card grid view.
 */
export const SubscriptionsListContent = () => {
  const { t } = useTranslation('subscriptions');
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: blocks, isLoading, isFetching, isError, refetch } = useSubscriptionBlocks();
  const areActionsLocked = useAreSubscriptionActionsLocked();

  // "Verifying" window: drives the button spinner while we wait out the confirm delay
  // before refetching (see CONFIG_REFRESH_DELAY_MS).
  const [isRefreshingConfig, setIsRefreshingConfig] = useState(false);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Clear the pending refresh timer if the list unmounts mid-delay (e.g. navigation).
  useEffect(() => () => clearTimeout(refreshTimeoutRef.current), []);

  // A single subscription over a single store has nothing to choose from: the list would only be a
  // step in the way, so it hands over to the detail without leaving an entry to come back to.
  const redirectLogged = useRef(false);
  useEffect(() => {
    if (redirectLogged.current || blocks?.length !== 1 || blocks[0].stores.length !== 1) return;
    redirectLogged.current = true;
    subLog.info('redirectToSingleSubscription', { 'block.uuid': blocks[0].uuid });
    history.replace(`/subscriptions/blocks/${blocks[0].uuid}`);
  }, [blocks, history]);

  const viewLogged = useRef(false);
  useEffect(() => {
    if (viewLogged.current || !blocks) return;
    viewLogged.current = true;
    subLog.info('viewSubscriptionsList', {
      'block.count': blocks.length,
      promoBlockCount: blocks.filter(isPromoBlock).length,
    });
  }, [blocks]);

  const filteredBlocks = useMemo(() => blocks?.filter((block) => matchesSearchTerm(block, searchTerm)), [
    blocks,
    searchTerm,
  ]);

  const anyConfiguring = useMemo(() => isAnyBlockConfiguring(blocks), [blocks]);

  const handleRefresh = () => {
    if (isRefreshingConfig || isFetching) return;
    subLog.info('refreshConfiguringSubscriptions');
    setIsRefreshingConfig(true);
    refreshTimeoutRef.current = setTimeout(() => {
      setIsRefreshingConfig(false);
      refetch();
    }, CONFIG_REFRESH_DELAY_MS);
  };

  const handleManageClick = (blockUuid: string) => {
    history.push(`/subscriptions/blocks/${blockUuid}`);
  };

  const handleGroupSubscriptions = () => {
    subLog.info('openBlockWizard', {
      flow: 'blockCreation',
      step: 'entry',
      source: 'subscriptionsList',
    });
    history.push('/subscriptions/blocks/create');
  };

  const handleRetry = () => {
    subLog.warn('retrySubscriptionsList');
    refetch();
  };

  const hasBlocks = Boolean(blocks);
  const hasNoResults = !isLoading && hasBlocks && filteredBlocks?.length === 0;
  const hasStaleDataError = isError && hasBlocks;

  return (
    <div className="flex flex-col gap-4">
      <SectionToolbar
        actions={
          <>
            <div className="relative w-full md:w-auto md:min-w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                disabled={anyConfiguring}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('list.searchPlaceholder')}
                value={searchTerm}
              />
            </div>
            <Button disabled={anyConfiguring || areActionsLocked} onClick={handleGroupSubscriptions} variant="outline">
              <Group />
              {t('list.groupSubscriptions')}
            </Button>

            {anyConfiguring && (
              <Button disabled={isRefreshingConfig || isFetching} onClick={handleRefresh}>
                <RefreshCw className={cn('mr-2 h-4 w-4', { 'animate-spin': isRefreshingConfig || isFetching })} />
                {t('list.refresh')}
              </Button>
            )}
          </>
        }
        title={t('list.title')}
      />

      {!isLoading && blocks && <ChurnedSubscriptionsBanner blocks={blocks} />}

      <div className="flex flex-col gap-4">
        {isLoading && <SubscriptionBlockCardSkeleton />}

        {!isLoading && isError && !hasBlocks && (
          <ErrorState
            description={t('list.errorDescription')}
            onRetry={handleRetry}
            retryLabel={t('list.retry')}
            title={t('list.errorLoading')}
          />
        )}

        {hasStaleDataError && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-4 py-2.5">
            <p className="text-sm text-muted-foreground">{t('list.errorLoading')}</p>
            <Button onClick={handleRetry} size="sm" variant="outline">
              {t('list.retry')}
            </Button>
          </div>
        )}

        {hasNoResults && <EmptyState description={t('list.emptySearchDescription')} title={t('list.emptySearch')} />}

        {!isLoading &&
          filteredBlocks?.map((block) => (
            <SubscriptionBlockCard
              block={block}
              disableManage={anyConfiguring && block.configEnabled}
              isConfiguring={!block.configEnabled}
              key={block.uuid}
              onManageClick={handleManageClick}
            />
          ))}
      </div>
    </div>
  );
};
